import { X } from 'lucide-react';
import { Button } from '../../../../components/Button';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { Tooltip } from '../../../../components/Tooltip';
import { COMPETENCY_LEVEL_LABELS, type CompetencyLevel } from '../../../human-resources/personnel/personnelTypes';
import { blankNeed, isBlankNeed, needProblem, type DraftNeed } from '../utils/developmentNeeds';

/** The ladder in its own order — Not Rated first, then up. `sort="none"` keeps it so. */
const LEVELS = ([0, 1, 2, 3, 4] as CompetencyLevel[]).map((l) => ({ value: String(l), label: COMPETENCY_LEVEL_LABELS[l] }));

// Not Rated is a starting point, never a target — the 201 file's rule for the same ladder.
const TARGET_LEVELS = LEVELS.filter((l) => l.value !== '0');

const ICON_BUTTON =
  'rounded-md p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200 disabled:cursor-not-allowed disabled:opacity-50';

/**
 * The Identified Development Needs — one BLOCK per competency, with labelled fields: the
 * competency, the level held, the level targeted, and the gap in the assessor's words. Ordinary
 * form fields, NOT the inline-add table (product call, 12 Oct 2026) — the questionnaire form's
 * question blocks are the shape. ONE component for the employee's own plan form and for Survey ›
 * Development Plan's Create, so the two cannot ask for a need two ways.
 *
 * **A plan takes ONE need** (product call, 12 Oct 2026). With one or none, its fields sit on the
 * form directly — no block, no Add — and a blank one is simply left out. A plan written before the
 * rule may hold several: those still show as numbered blocks that can be revised or removed, and
 * there is no Add while any is there. The server refuses a second either way. The field ids
 * are `${idPrefix}-needs.{row}.{field}` — what a refusal naming a need's row is focused by.
 */
export function DevelopmentNeedsGrid({
  idPrefix,
  needs,
  onChange,
  competencyOptions,
  disabled,
}: {
  idPrefix: string;
  needs: DraftNeed[];
  onChange: (next: DraftNeed[]) => void;
  /** The competencies offered on a block, given what that block already holds. */
  competencyOptions: (selected: string) => { value: string; label: string; sublabel?: string }[];
  disabled: boolean;
}) {
  // The one need the plan takes; several only on a plan written before the rule.
  const several = needs.length > 1;

  const update = (index: number, patch: Partial<DraftNeed>) =>
    onChange(needs.map((n, i) => (i === index ? { ...n, ...patch } : n)));

  return (
    <div className="flex flex-col gap-3">
      {needs.length === 0 ? <p className="text-xs text-slate-500">No development need yet.</p> : null}

      {needs.map((need, index) => {
        const problem = needProblem(need);
        // The need as a whole is optional; once it is started, its three fields are owed.
        const owed = several || !isBlankNeed(need) || need.currentLevel !== '' || need.targetLevel !== '';

        return (
          <div key={need.key} className={several ? 'flex flex-col gap-4 rounded-xl border border-slate-200 p-4' : 'flex flex-col gap-4'}>
            {several ? (
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm text-slate-800">Need {index + 1}</p>

                <Tooltip label="Remove">
                  <button
                    type="button"
                    onClick={() => onChange(needs.filter((_, i) => i !== index))}
                    disabled={disabled}
                    aria-label={`Remove need ${index + 1}`}
                    className={ICON_BUTTON}
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                </Tooltip>
              </div>
            ) : null}

            {/*
              Competency and Gap identified take a FULL LINE each (product call, 12 Oct 2026) — a
              competency's name and a written gap are both longer than half the form. The two
              levels are a pair, and stay side by side beneath.
            */}
            <Select
              id={`${idPrefix}-needs.${index}.competencyId`}
              label="Competency"
              required={owed}
              placeholder="Choose a competency"
              value={need.competencyId}
              onChange={(next) => update(index, { competencyId: next })}
              options={competencyOptions(need.competencyId)}
              remote
              // The one need can be emptied again — a blank one is left out.
              clearable={!several}
              disabled={disabled}
            />
            <TextField
              id={`${idPrefix}-needs.${index}.gap`}
              label="Gap identified"
              value={need.gap}
              onChange={(e) => update(index, { gap: e.target.value })}
              maxLength={1000}
              disabled={disabled}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Select
                id={`${idPrefix}-needs.${index}.currentLevel`}
                label="Current level"
                required={owed}
                placeholder="Choose a level"
                value={need.currentLevel}
                onChange={(next) => update(index, { currentLevel: next })}
                options={LEVELS}
                sort="none"
                clearable={false}
                disabled={disabled}
              />
              <Select
                id={`${idPrefix}-needs.${index}.targetLevel`}
                label="Target level"
                required={owed}
                placeholder="Choose a level"
                value={need.targetLevel}
                onChange={(next) => update(index, { targetLevel: next })}
                options={TARGET_LEVELS}
                sort="none"
                clearable={false}
                disabled={disabled}
                // The one rule that crosses two fields, said on the field that breaks it.
                error={problem === 'The target is below the current level.' ? problem : undefined}
              />
            </div>
          </div>
        );
      })}

      {/* Only where the plan holds NO need yet — never a second beside one. */}
      {needs.length === 0 ? (
        <div>
          <Button id={`${idPrefix}-add-need`} variant="secondary" onClick={() => onChange([blankNeed()])} disabled={disabled}>
            Add need
          </Button>
        </div>
      ) : null}
    </div>
  );
}
